import {useEffect} from 'react';
import {useAtomValue, useSetAtom} from 'jotai';
import {classes} from '@/util';
import {toastListAtom, addToastAtom, removeToastAtom} from '@/store/toast';
import styles from './style.module.css';

export interface ToastData {
    id: string;
    message: string;
    type?: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
}

interface ToastProps {
    id: string;
    message: string;
    type?: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
    onRemove: (id: string) => void;
}

function Toast({
    id,
    message,
    type = 'info',
    duration = 5000,
    onRemove,
}: ToastProps) {
    useEffect(() => {
        const timer = setTimeout(() => {
            onRemove(id);
        }, duration);

        return () => clearTimeout(timer);
    }, [id, duration, onRemove]);

    const handleClose = () => {
        onRemove(id);
    };

    return (
        <div className={classes(styles.toast, styles[type])}>
            <div className={styles.content}>
                <span className={styles.message}>{message}</span>
                <button
                    className={styles.closeButton}
                    onClick={handleClose}
                    aria-label="Close notification"
                >
                    ×
                </button>
            </div>
        </div>
    );
}

export function ToastContainer() {
    const toasts = useAtomValue(toastListAtom);
    const removeToast = useSetAtom(removeToastAtom);

    if (toasts.length === 0) return null;

    return (
        <div className={styles.container}>
            {toasts.map((toast) => (
                <Toast
                    key={toast.id}
                    id={toast.id}
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onRemove={removeToast}
                />
            ))}
        </div>
    );
}

export function useToast() {
    const addToast = useSetAtom(addToastAtom);

    return {
        showToast: (toast: Omit<ToastData, 'id'>) => addToast(toast),
        showSuccess: (message: string) => addToast({message, type: 'success'}),
        showError: (message: string) => addToast({message, type: 'error'}),
        showWarning: (message: string) => addToast({message, type: 'warning'}),
        showInfo: (message: string) => addToast({message, type: 'info'}),
    };
}
